import { useQuery } from '@tanstack/react-query';
import { financialInstrumentQueryKey } from './keys';
import { normalizeFinancialInstrument } from '../types';
import type {
  FinancialInstrumentDto,
  FinancialInstrumentHttpRequest,
  RawFinancialInstrumentDto,
} from '../types';

export interface UseFinancialInstrumentParams {
  documentId: string;
  /**
   * Endpoint GET del IF activo (BRECHA GAP-1 — el BE aún no lo expone). Si se
   * omite, la query queda deshabilitada y el formulario se puebla solo con
   * `initialData` / mutaciones.
   */
  financialInstrumentEndpoint?: string;
  httpRequest: FinancialInstrumentHttpRequest;
  /** Datos iniciales (p.ej. provenientes de la sección FactBox del adapter). */
  initialData?: FinancialInstrumentDto | null;
  enabled?: boolean;
}

/**
 * Carga el Instrumento Financiero activo del documento (o `null` si no hay).
 *
 * Cache key: `['financial-instrument', documentId]`. El backend puede responder
 * con `204`/cuerpo vacío/`404` cuando no hay IF activo: cualquiera de esos casos
 * se normaliza a `null` para que el panel decida crear-vs-editar.
 */
export function useFinancialInstrument({
  documentId,
  financialInstrumentEndpoint,
  httpRequest,
  initialData,
  enabled = true,
}: UseFinancialInstrumentParams) {
  return useQuery<FinancialInstrumentDto | null>({
    queryKey: financialInstrumentQueryKey(documentId),
    enabled: enabled && Boolean(documentId) && Boolean(financialInstrumentEndpoint),
    initialData: initialData ?? undefined,
    queryFn: async () => {
      try {
        const raw = await httpRequest<RawFinancialInstrumentDto | null>(
          'GET',
          financialInstrumentEndpoint!,
        );
        return raw && raw.id ? normalizeFinancialInstrument(raw) : null;
      } catch (error) {
        // "Sin IF activo" se modela como 404 → null (no es un error de carga).
        if ((error as { status?: number })?.status === 404) return null;
        throw error;
      }
    },
  });
}
